import { createTheme, Theme } from '@mui/material/styles';
import palette from './palette';
import typography from './typography';
import shadows from './shadows';
import { borderRadius, transitions } from './variables';
import './fonts.css';

const theme: Theme = createTheme({
  palette,
  typography,
  shadows,
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
          margin: 0,
          padding: 0,
        },
        html: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
        },
        body: {
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
          backgroundColor: palette.background.default,
          color: palette.text.primary,
          transition: 'all 0.3s ease',
        },
        '#root': {
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '0.4285rem',
          textTransform: 'uppercase',
          fontWeight: 600,
          padding: '10px 24px',
          fontSize: '0.875rem',
          lineHeight: 1.5,
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 7px 14px rgba(50,50,93,.1), 0 3px 6px rgba(0,0,0,.08)',
          },
        },
        contained: {
          boxShadow: '0 4px 6px rgba(50,50,93,.11), 0 1px 3px rgba(0,0,0,.08)',
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: palette.background.paper,
          borderRadius: '0.4285rem',
          boxShadow: '0 1px 20px 0 rgba(0,0,0,.1)',
          color: palette.text.primary,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '1.25rem',
        },
        title: {
          fontSize: '1.0625rem',
          fontWeight: 600,
          color: palette.text.primary,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '1.25rem',
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: palette.grey[100],
          '.MuiTableCell-root': {
            color: palette.grey[800],
            fontSize: '0.875rem',
            fontWeight: 600,
            lineHeight: 1,
            padding: '12px 16px',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${palette.divider}`,
          padding: '12px 16px',
          fontSize: '0.875rem',
        },
        head: {
          color: palette.text.primary,
          fontWeight: 600,
          backgroundColor: 'transparent',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(34, 42, 66, 0.1)',
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
          overflow: 'hidden',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
        },
        standardSuccess: {
          backgroundColor: palette.success.lighter,
          color: palette.success.dark,
        },
        standardInfo: {
          backgroundColor: palette.info.lighter,
          color: palette.info.dark,
        },
        standardWarning: {
          backgroundColor: palette.warning.lighter,
          color: palette.warning.dark,
        },
        standardError: {
          backgroundColor: palette.danger.lighter,
          color: palette.danger.dark,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 500,
          color: palette.grey[700],
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          marginRight: '16px',
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: palette.primary.lighter,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: '0.4285rem',
          backgroundColor: palette.background.paper,
          border: `1px solid ${palette.divider}`,
          fontSize: '0.875rem',
          transition: 'all 0.2s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
          '&:focus': {
            borderColor: palette.primary.main,
            boxShadow: `0 3px 9px rgba(50,50,9,0), 3px 4px 8px rgba(94,114,228,.1)`,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.grey[300],
          },
          '&.Mui-disabled': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.action.disabledBackground,
            },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: palette.grey[800],
        },
        arrow: {
          color: palette.grey[800],
        },
      },
    },
  },
});

export default theme; 